import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/api/client';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import { CheckCircle, Clock, Star } from 'lucide-react';

export default function MembershipApply() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [statement, setStatement] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/membership/apply', { statement });
      await refreshUser();
      toast.success('Application submitted!');
      navigate('/membership/status');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ backgroundColor: 'var(--bg-subtle)', minHeight: '100vh' }}>
      <Navbar />
      <div className="container-app py-10">
        <div className="grid lg:grid-cols-3 gap-8 max-w-4xl mx-auto">
          {/* Form */}
          <div className="lg:col-span-2">
            <div className="mb-8">
              <p className="text-xs uppercase tracking-widest mb-2" style={{ color: 'var(--gold)' }}>Step 2 of 2</p>
              <h1 className="text-2xl lg:text-3xl font-semibold mb-2" style={{ fontFamily: 'var(--font-serif)', color: 'var(--text)' }}>Apply for Membership</h1>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Share a brief statement about why you’d like to join Messums Art Market.</p>
            </div>

            <div className="p-8 rounded-2xl border" style={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border-clr)' }}>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Membership Statement (optional but recommended)</Label>
                  <Textarea
                    placeholder="Tell us about your art background, what you plan to buy or sell, and why you’d like to join our community..."
                    value={statement}
                    onChange={e => setStatement(e.target.value)}
                    rows={6}
                    style={{ borderColor: 'var(--border-clr)' }}
                  />
                  <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>A thoughtful statement significantly increases your chances of approval.</p>
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="membership-apply-submit-button" style={{ backgroundColor: 'var(--gold)', color: 'white', borderRadius: '10px' }}>
                  {loading ? 'Submitting...' : 'Submit Application'}
                </Button>
              </form>
            </div>
          </div>

          {/* What to expect */}
          <div>
            <div className="p-6 rounded-2xl border" style={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border-clr)' }}>
              <h3 className="text-base font-semibold mb-4" style={{ fontFamily: 'var(--font-serif)', color: 'var(--text)' }}>What happens next</h3>
              {[{icon: Clock, t: 'Review (1–3 days)', d: 'Our team personally reviews each application.'},{icon: CheckCircle, t: 'Decision', d: 'You’ll receive an email with the outcome.'},{icon: Star, t: 'Full Access', d: 'Approved members get access to seller details, listing creation, and enquiries.'}].map(({icon: Icon, t, d}) => (
                <div key={t} className="flex gap-3 mb-4">
                  <Icon size={16} className="flex-shrink-0 mt-0.5" style={{ color: 'var(--gold)' }} />
                  <div>
                    <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>{t}</p>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
